import { useDispatch, useSelector } from 'react-redux';
import { OysterSummaryTabs } from './OysterSummaryPage';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { useMemo, useState } from 'react';
import { selectAccount, selectSettings } from '../../store/auth/auth.selector';
import { selectCropSpecies } from '../../store/utils/utils.selector';
import {
  calcDiffDays,
  getAverageSize,
  pcsToDoz,
  defaultDateFormat,
  displayValueUnit,
} from '../../lib/common.helpers';
import {
  calcOnGrowWastePercent,
  calcOysterPercent,
} from '../../lib/farm.helpers';
import { Button, Table } from 'antd';
import { useHistory } from 'react-router-dom';
import { Dropdown } from '../../components/shared';
import { translate } from '../../lib/lang.helper';
import { selectLang } from '../../store/ui/ui.selector';
import { ILineResource } from '../../entities/farms.entities';
import AssessmentModal from '../../components/farm-modals/AssessmentModal';
import { updateUserSetting } from '../../store/auth/auth.actions';

interface ISpecie {
  name: string;
  amount: number;
}

interface IRow {
  farm_id: number;
  farm_name: string;
  line_id: number;
  line_name: string;
  seed_id: number;
  date_seeded: string;
  age: number;
  last_checked: string;
  last_size: number;
  species: ISpecie[];
  ready_to_harvest: string | null;
}

const basicColumns = (unitType: any) => [
  { key: 'farm_name', title: 'Farm', render: (x: IRow) => x.farm_name },
  { key: 'line_name', title: 'Line', render: (x: IRow) => x.line_name },
  {
    key: 'date_seeded',
    title: 'Date Seeded',
    render: (x: IRow) => x.date_seeded,
  },
  {
    key: 'age',
    title: "Today's Age",
    render: (x: IRow) => <span>{`${x.age} day(s)`}</span>,
  },
  {
    key: 'last_checked',
    title: 'Last Checked',
    render: (x: IRow) => x.last_checked,
  },
  {
    key: 'last_size',
    title: 'Last Known Size',
    render: (x: IRow) => (
      <span>{displayValueUnit(x.last_size, unitType, 'size_mm')}</span>
    ),
  },
  {
    key: 'species',
    title: 'Species',
    render: (x: IRow) => (
      <div>
        {x.species.map((specie, index) => (
          <div key={index} className='d-flex justify-between'>
            <span>{specie.name}</span>
            <span>{`${specie.amount} dozens`}</span>
          </div>
        ))}
      </div>
    ),
  },
];

interface IFilter {
  farmIDs: number[];
  lineIDs: number[];
  status: 'ready' | 'not-ready' | 'all';
}

interface IAssessLine {
  lineData: ILineResource;
  size_min: number;
  size_max: number;
  size_avg: number;
}

const OysterSeedingsPage = () => {
  const history = useHistory();
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const oysterSpecies = useSelector(selectCropSpecies);
  const accountSetting = useSelector(selectAccount);
  const onGrowWaste = accountSetting?.oyster_crops;
  const oysterFarms = useSelector(selectFarmsData);
  const pageFilter = useSelector(selectSettings).page_filter ?? {};

  const [filter, setFilter] = useState<IFilter>({
    farmIDs: pageFilter.farmIDs ?? [],
    lineIDs: pageFilter.lineIDs ?? [],
    status: pageFilter.status ?? 'all',
  });
  const [assessLine, setAssessLine] = useState<IAssessLine>();

  const updateFilter = (key: keyof IFilter, value: any) => {
    let tmp = { ...filter, [key]: value };
    if (key === 'farmIDs') {
      tmp.lineIDs = [];
    }
    dispatch(updateUserSetting('page_filter', tmp));
    setFilter(tmp);
  };
  const readyLineClick = (farmID: number, lineID: number, seedID: number) => {
    const farm = oysterFarms.find(x => x.id === farmID);
    const line = farm?.lines.find(x => x.id === lineID);
    if (!line?.growing_cycle) return;

    const seeding = line.growing_cycle.seedings.find(x => x.id === seedID);
    if (!seeding) return;

    let size_min = seeding.spat_size,
      size_max = seeding.spat_size_max ?? seeding.spat_size,
      size_avg = getAverageSize(seeding.spat_size, seeding.spat_size_max);

    if (line.growing_cycle.last_assessment) {
      size_min = line.growing_cycle.last_assessment.shell_size.min;
      size_max = line.growing_cycle.last_assessment.shell_size.max;
      size_avg = line.growing_cycle.last_assessment.shell_size.avg;
    }

    setAssessLine({ lineData: line, size_min, size_max, size_avg });
  };

  const data = useMemo(() => {
    let result: IRow[] = [];

    for (let farm of oysterFarms) {
      for (let line of farm.lines) {
        if (!line.growing_cycle) continue;
        const cycle = line.growing_cycle;
        const lastAssessment = cycle.last_assessment;

        const growPercent = calcOnGrowWastePercent(
          onGrowWaste,
          'grow',
          line.id,
        );
        const wastePercent = calcOnGrowWastePercent(
          onGrowWaste,
          'waste',
          line.id,
        );
        const speciePercents = [
          {
            name: 'Grown ons',
            percent: growPercent === undefined ? 0 : growPercent,
          },
          {
            name: 'Waste',
            percent: wastePercent === undefined ? 0 : wastePercent,
          },
          ...oysterSpecies.map(specie => ({
            name: specie.name,
            percent: calcOysterPercent(specie, line.id) ?? 0,
          })),
        ];
        let i = 0;
        for (let seed of cycle.seedings) {
          const species = speciePercents
            .map(specie => ({
              name: specie.name,
              amount: pcsToDoz((seed.spat_amount * specie.percent) / 100),
            }))
            .filter(specie => specie.amount > 0);

          result.push({
            farm_id: farm.id,
            farm_name: i === 0 ? farm.name : '',
            line_id: line.id,
            line_name: i === 0 ? line.line_name : '',
            seed_id: seed.id,
            date_seeded: defaultDateFormat(seed.planned_date_seed) ?? '-',
            age: calcDiffDays(seed.planned_date_seed, Date.now()),
            last_checked:
              defaultDateFormat(lastAssessment?.assessment_date) ?? '-',
            last_size: lastAssessment
              ? lastAssessment.shell_size.avg
              : getAverageSize(seed.spat_size, seed.spat_size_max),
            species,
            ready_to_harvest: lastAssessment?.ready_harvest
              ? defaultDateFormat(lastAssessment?.ready_harvest)
              : null,
          });
          i++;
        }
      }
    }
    return result;
  }, [oysterFarms]);

  const filtered = useMemo(() => {
    let result = [...data];
    if (filter.farmIDs.length > 0) {
      result = result.filter(x => filter.farmIDs.includes(x.farm_id));
    }
    if (filter.lineIDs.length > 0) {
      result = result.filter(x => filter.lineIDs.includes(x.line_id));
    }
    if (filter.status === 'ready') {
      result = result.filter(x => !!x.ready_to_harvest);
    } else if (filter.status === 'not-ready') {
      result = result.filter(x => !x.ready_to_harvest);
    }
    return result;
  }, [data, filter]);

  const linesOptions =
    filter.farmIDs.length <= 0
      ? undefined
      : oysterFarms
          .filter(farm => filter.farmIDs.includes(farm.id))
          .flatMap(farm => farm.lines)
          .map(line => ({
            id: line.id.toString(),
            value: line.id.toString(),
            label: `${line.line_name} (${line.farm_name})`,
          }));

  const columns = [
    ...basicColumns(accountSetting?.display_unit),
    {
      key: 'ready_to_harvest',
      title: 'Status',
      render: (x: IRow) => (
        <div onClick={e => e.stopPropagation()}>
          {!x.ready_to_harvest ? (
            <Button
              size='small'
              type='primary'
              onClick={() => readyLineClick(x.farm_id, x.line_id, x.seed_id)}
            >
              {translate(lang, 'Ready to harvest')}
            </Button>
          ) : (
            <span>
              <div>{translate(lang, 'Ready to harvest')}</div>
              <div>{`on ${x.ready_to_harvest}`}</div>
            </span>
          )}
        </div>
      ),
      align: 'left',
    } as any,
  ];

  return (
    <div className='bg-secondary min-h-100'>
      <div className='container'>
        <OysterSummaryTabs />
        <div className='mt-24 d-flex'>
          <div className='mr-7' style={{ width: '220px' }}>
            <Dropdown
              mode='multiple'
              label={translate(lang, 'Filter by farm')}
              options={oysterFarms.map(x => ({
                id: x.id.toString(),
                value: x.id.toString(),
                label: x.name,
              }))}
              value={filter.farmIDs.map(String) as any}
              onChange={(v: any) => updateFilter('farmIDs', v.map(Number))}
            />
          </div>
          <div className='mr-7 ml-7' style={{ width: '220px' }}>
            <Dropdown
              mode='multiple'
              label={translate(lang, 'Filter by line')}
              options={linesOptions ?? []}
              value={filter.lineIDs.map(String) as any}
              onChange={(v: any) => updateFilter('lineIDs', v.map(Number))}
              disabled={!linesOptions}
            />
          </div>
          <div className='ml-7' style={{ width: '220px' }}>
            <Dropdown
              label={translate(lang, 'Line Status')}
              options={[
                { id: 'all', value: 'all', label: 'All' },
                { id: 'ready', value: 'ready', label: 'Ready to Harvest' },
                {
                  id: 'not-ready',
                  value: 'not-ready',
                  label: 'Not Ready to Harvest',
                },
              ]}
              value={filter.status}
              onChange={(v: any) => updateFilter('status', v)}
            />
          </div>
        </div>
        <div className='w-100 mt-17 pb-32'>
          <Table
            rowKey={(x, i) => i?.toString() ?? x.farm_id}
            className='table table--isFarm'
            pagination={false}
            columns={columns}
            dataSource={filtered}
            onRow={row => ({
              onClick: () =>
                history.push(`/farms/${row.farm_id}/${row.line_id}`),
            })}
          />
        </div>
      </div>
      {!!assessLine && (
        <AssessmentModal
          visible={!!assessLine}
          onCancel={() => setAssessLine(undefined)}
          onConfirm={() => setAssessLine(undefined)}
          data={{
            ready_to_harvest: true,
            size_min: assessLine.size_min,
            size_avg: assessLine.size_avg,
            size_max: assessLine.size_max,
          }}
          lineData={assessLine.lineData}
        />
      )}
    </div>
  );
};

export default OysterSeedingsPage;
